import angular, { IPromise, IServiceProvider } from "angular";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import {
  ProcessGroup,
  ProcessGroupsResponse,
  ProcessInformation,
} from "../model/ProcessGroups";
import {
  UserProfile,
  vsName as UserProfileName,
} from "../../application/UserProfile";
import { ProcessGroupConfig } from "../config/ProcessGroupConfig";
import { ServiceResponse } from "../../records/server/query";
import { ProcessApiResponse } from "../model/ProcessApi";
import { StateService } from "@uirouter/angularjs";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import { ColDef } from "../../records/grids/columns/colDef";

export const vsServiceName = "processInformationService";
export const vsProviderName = `${vsServiceName}Provider`;

interface ProcessGroupRuntime extends ProcessGroup {
  groupConfig?: ProcessGroupConfig;
  gridSettingId: symbol;
}

export class ProcessInformationServiceProvider implements IServiceProvider {
  private runtimeGroupConfigs: {
    [name: string]: ProcessGroupConfig | undefined;
  } = {};

  addRuntimeConfig(groupConfig: ProcessGroupConfig) {
    this.runtimeGroupConfigs[groupConfig.processGroupId] = groupConfig;
  }

  $get = [
    ServerUtilityName,
    UserProfileName,
    "$state",
    "$injector",
    GridSettingsServiceName,
    "processGroups",
    (
      serverUtility: ServerUtility,
      userProfile: UserProfile,
      $state: StateService,
      $injector: angular.auto.IInjectorService,
      gridSettingsService: GridSettingsService,
      processGroups: ProcessGroup[]
    ) => {
      const runtimeGroups = processGroups.map((g) => <ProcessGroupRuntime>g);
      runtimeGroups.forEach((pg) => {
        const runtimeGroup = <ProcessGroupRuntime>pg;
        const groupConfig = (runtimeGroup.groupConfig =
          this.runtimeGroupConfigs[runtimeGroup.Id]);
        if (!groupConfig) {
          throw new Error("Could not find process group configuration");
        }
        runtimeGroup.gridSettingId = Symbol(
          `processes.${groupConfig.processGroupId}`
        );
      });

      return new ProcessInformationService(
        runtimeGroups,
        userProfile,
        $state,
        serverUtility,
        $injector,
        gridSettingsService
      );
    },
  ];
}

export class ProcessInformationService {
  constructor(
    private readonly processGroups: ProcessGroupRuntime[],
    private readonly userProfile: UserProfile,
    private readonly $state: StateService,
    private readonly serverUtility: ServerUtility,
    private readonly $injector: angular.auto.IInjectorService,
    private readonly gridSettingsService: GridSettingsService
  ) {}

  static defaultAdditionalGridColumns = [() => []];
  static defaultDefaultQuery = [() => undefined];

  getProcessGroups() {
    return this.processGroups;
  }

  getProcessGroup(id: string) {
    return this.processGroups.find(
      (group) => group.Id.toLowerCase() === id.toLowerCase()
    );
  }

  getAuthorizedGetProcessGroups() {
    return this.processGroups.filter((pg) =>
      this.hasPermission(pg.GetPermission)
    );
  }

  getAuthorizedCreateProcessGroups() {
    return this.processGroups.filter((pg) =>
      this.hasPermission(pg.CreatePermission)
    );
  }

  createProcess(processInformation: ProcessInformation, createDto: any) {
    return this.serverUtility
      .sendApiRequest<ServiceResponse<ProcessApiResponse<any>>>({
        method: "post",
        url: `/api${processInformation.Url}`,
        data: { Data: createDto },
      })
      .then((r) => r.Result);
  }

  getCreateDto(
    processGroup: ProcessGroup,
    process: ProcessInformation,
    dataObject: any
  ) {
    const groupConfig = (<ProcessGroupRuntime>processGroup).groupConfig!;
    return this.$injector.invoke(groupConfig.getCreateProcessDto, null, {
      processGroup,
      process,
      dataObject,
    });
  }

  selectDataObject(processGroup: ProcessGroup, process: ProcessInformation) {
    const groupConfig = (<ProcessGroupRuntime>processGroup).groupConfig!;
    return this.$injector.invoke(groupConfig.onSelectDataObject, null, {
      processGroup,
      process,
    });
  }

  navigateToDetail(processResponse: ProcessApiResponse<any>) {
    const groupId = processResponse.GroupId;
    const processInstancesId = processResponse.Id;

    return this.$state.go("main.processes.grid.group.detail", {
      processInstancesId,
      groupId,
    });
  }

  getGridSettings(processGroup: ProcessGroup): IGridSettings {
    return this.gridSettingsService.getGridSettings(
      (<ProcessGroupRuntime>processGroup).gridSettingId
    );
  }

  getAdditionalGridColumns(processGroup: ProcessGroup): ColDef[] {
    const groupConfig = (<ProcessGroupRuntime>processGroup).groupConfig!;
    return this.$injector.invoke(
      groupConfig.additionalGridColumns ??
        ProcessInformationService.defaultAdditionalGridColumns,
      null,
      {
        processGroup,
      }
    );
  }

  getDetailTemplate(processId: string) {
    const processGroup = this.getProcessGroup(processId);
    return `<process-detail
process-information="processInformation"
process-group="processGroup"
process="process"
>
<info-header>${processGroup?.groupConfig?.processDetailTemplate}</info-header>
</process-detail>`;
  }

  createNewProcessFlow(
    processGroup: ProcessGroup,
    process: ProcessInformation
  ) {
    return this.selectDataObject(processGroup, process)
      .then((dataObject) =>
        this.getCreateDto(processGroup, process, dataObject)
      )
      .then((createDto) => this.createProcess(process, createDto));
  }

  private hasPermission(permission: string) {
    return !permission || this.userProfile.hasPermission(permission);
  }
}
